<template>
  <div>
    <page-header
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    >
      <template #customActions>
        <action-button
          id="btnGoBack"
          class="mr-1"
          :disabled="isDisabled"
          i="fas fa-arrow-circle-left fa-fw"
          text="Regresar"
          variant="outline-success"
          @click="onGoBackHandler"
        />
        <action-button
          v-if="canCancelContract"
          class="mr-1"
          :disabled="isDisabled"
          i="fas fa-times-circle"
          text="Cancelar Contrato"
          variant="outline-danger"
          @click="cancelContract"
        />
        <template v-if="showSaveAndSubmit">
          <action-button
            v-if="!isReadOnly"
            class="mr-1"
            :disabled="isDisabled"
            i="fas fa-save"
            text="Guardar"
            variant="success"
            @click="saveHandler"
          />
          <action-button
            v-if="!isReadOnly"
            class="mr-1"
            :disabled="isDisabled"
            i="fas fa-flag"
            text="Someter"
            variant="outline-success"
            @click="submitHandler"
          />
        </template>
        <action-button
          v-if="canDownloadContract"
          class="mr-1"
          :disabled="isDisabledPrintAndSignButtons"
          i="fas fa-print"
          text="Imprimir"
          variant="success"
          @click="onDownloadContract"
        />
        <action-button
          v-if="canEditParticipants"
          class="mr-1"
          :disabled="isDisabled"
          i="fas fa-file-signature"
          :text="!specialParticipantsEdit ? 'Editar Participantes' : 'Guardar'"
          variant="success"
          @click="editParticipants"
        />
        <action-button
          v-if="canCreateSignDocument && !specialParticipantsEdit"
          class="mr-1"
          :disabled="isDisabled"
          i="fas fa-file-signature"
          text="Crear Documento para firmar"
          variant="success"
          @click="createDocumentForSignature"
        />
        <template v-if="!contractInformation.isElectronicsSignature">
          <action-button
            v-if="canSignContract"
            class="mr-1"
            :disabled="isDisabledPrintAndSignButtons"
            i="fas fa-signature"
            text="Firmar Contrato"
            variant="success"
            @click="onSignContractHandler"
          />
        </template>
        <action-button
          v-if="canCancelElectronicSignature"
          class="mr-1"
          i="fas fa-times-circle"
          text="Cancelar Firma Electrónica"
          variant="outline-danger"
          @click="cancelElectronicSignature"
        />
        <action-button
          v-if="userCanAmendContract && canAmendContract && !viewAmendContract"
          class="mr-1"
          :disabled="isDisabled"
          i="fas fa-file-signature"
          text="Enmendar Contrato"
          variant="success"
          @click="onAmendContractHandler"
        />
        <drop-down
          v-if="canApproveContract || canDenyContract"
          class="w-sm-100 dropdown-custom-action"
          :disabled="isDisabled"
          icon="fas fa-flag-alt"
          :items="determinationActionItems"
          title="Determinar"
        />
      </template>
    </page-header>
    <contract-amendment
      v-if="viewAmendContract"
      class="mb-3"
      :data="amendContractData"
      :reason-types="catalog.amendmentReasons"
      @on-cancel="viewAmendContract = false"
      @on-continue="onCreateAmendContractHandler"
    />
    <contract-amendment
      v-if="isAnAmendment"
      class="mb-3"
      :data="amendReadOnlyContractData"
      :is-read-only="true"
      :reason-types="catalog.amendmentReasons"
    />
    <validation-observer ref="contractDeterminationForm" tag="form">
      <contract-determination
        v-if="showContractDetermination"
        class="mb-4"
        :data="determinationData"
        @cancel="onCancelDetermination"
        @download-document="onDownloadDeterminationDocumentHandler"
        @save="onSaveContractDetermination"
      />
    </validation-observer>
    <validation-observer ref="cancelElectronicSignatureForm" tag="form">
      <cancel-electronic-signature
        v-if="showCancelElectronicSignature"
        class="mb-4"
        :data="cancelElectronicSignatureData"
        @cancel="onCancelElectronicSignature"
        @save="onSaveCancelElectronicSignature"
      />
    </validation-observer>

    <div v-if="canCommentDeniedContract || deniedComment != null">
      <custom-callout border-color="orange" :has-toggle="false">
        <div class="ml-n0 ml-md-n3 mr-3">
          <icon-view
            icon="mb-1 ml-1 fa fa-file-signature"
            icon-background="#fff"
            icon-color="#FF7058"
            icon-size="14px"
            title="Comentario"
          />
          <div class="mt-3">
            <span class="color-gray-6 d-block font-14 mb-1">
              Comentario:
            </span>
            <custom-text-area
              id="comment"
              v-model="deniedComment"
              :count-classes="['float-right']"
              :disabled="!canCommentDeniedContract"
              :maxlength="3000"
              show-char-count
            />
          </div>
        </div>
      </custom-callout>
      <br />
    </div>
    <general-info :contract-info="contractHeaderInfo || {}" />
    <custom-tab class="custom-tabs">
      <custom-tab-item
        :active="tabIndex === ContactInformationTabIndex"
        :lazy="false"
        title="Información de Contrato"
        @click="onChangeInfoTab(ContactInformationTabIndex)"
      >
        <validation-observer ref="contractManagementForm" tag="form">
          <location-info
            class="mt-4"
            :is-read-only="isReadOnlyForLocationInfo"
            :location-info="locationInfo || {}"
            :location-types="catalog.locationTypes"
            :locations="catalog.locations"
            :municipalities="catalog.municipalities"
            @match-location="matchLocation"
            @on-change-location="onChangeLocationHandler"
            @on-change-location-type="onChangeLocationTypeHandler"
            @on-change-municipality="onChangeMunicipality"
          />
          <validation-observer ref="contractManagementForm4" tag="form">
            <RequestEducationalServiceInfoContract
              v-model="studentInformation.location"
              :default-value="locationDefaultValue"
              :is-read-only="isReadOnly"
              :location-types="locationTypes"
              @input="onChangeEducationalServiceInfoModelHandler"
            />
          </validation-observer>
          <validation-observer ref="contractManagementForm2" tag="form">
            <contract-information
              ref="contractInformationRef"
              :company-id="this.companyId"
              :contract-academic-year-id="academicYearId"
              :contract-id="contractId"
              :contract-info-body="contractInfoBody"
              :contract-information-model="contractInformationModel"
              :contract-status-id="contractStatusId"
              :discipline-type-id="
                contractHeaderInfo.requestServiceDisciplineTypeId
              "
              :info="contractInformation"
              :intervention="interventionServiceModel"
              :is-assistance-service-type="contractInfo.isAssistanceServiceType"
              :is-electronics-signature="
                contractInformation.isElectronicsSignature
              "
              :is-read-only="isReadOnly"
              :is-therapy-service-type="contractInfo.isTherapyServiceType"
              :location="contractInfo.locationInfo"
              :location-match="locationMatch"
              :location-type="locationInfo.locationType?.id"
              :municipalities="catalog.municipalities"
              :request-service-id="requestServiceId"
              :sub-discipline-type-id="subDisciplineTypeId"
              @input="onChangeContractInformationModelHandler"
            />
          </validation-observer>
        </validation-observer>
        <section-title
          class="mt-3"
          content="Datos sobre la Intervención"
          :has-line="true"
        />

        <intervention-info
          v-model="interventionInfo.specialists"
          :added-specialists="contractInfo.specialists"
          :added-supervisors="contractInfo.supervisors"
          class="my-4"
          :discipline-require-supervisor="
            contractHeaderInfo.disciplineRequiresSupervisor
          "
          :discipline-type-id="
            contractHeaderInfo.requestServiceDisciplineTypeId
          "
          :is-read-only="isReadOnlyForInterventionInfo"
          :max-specialist-per-contract="contractInfo.maxSpecialistPerContract"
          :show-cost-transportation="
            contractInformationModel.hasTransportationCost && isSchoolOrHome
          "
          :specialists="catalog.specialists"
          :supervisors="catalog.supervisors"
          :transportation-cost="contractInformationModel.transportationCost"
          @add="addInterventionInfoSpecialist"
          @delete="removeInterventionInfoSpecialist"
          @select-specialist="getSpecialistSupervisors"
        />
        <validation-observer ref="contractManagementForm3" tag="form">
          <intervention-service
            :amendment-contract="amendContractData"
            class="mx-2"
            :compensatory-service-count="
              contractInfo.compensatoryServicePendingBalance
            "
            :contract-id="contractId"
            :discipline-type-id="
              contractHeaderInfo.requestServiceDisciplineTypeId
            "
            :durations="catalog.durations"
            :frequencies="catalog.frequencies"
            :has-invalid-service="contractInfo.hasInvalidService"
            :is-assistance-service-type="contractInfo.isAssistanceServiceType"
            :is-evaluation-service-type="contractInfo.isEvaluationServiceType"
            :is-read-only="isReadOnlyForInterventionService"
            :is-therapy-service-type="contractInfo.isTherapyServiceType"
            :is-valid-cost="isValidCost"
            :modalities="catalog.modalities"
            :pei-info="peiInfo"
            :service-discipline-name="serviceDisciplineName"
            :service-info.sync="serviceInfo"
            :service-info-match-with-pei="interventionServiceInfoMatchWithPei"
            :sub-discipline-type-id.sync="contractInfo.subDisciplineTypeId"
            @input="onChangeServiceModelHandler"
            @on-change-duration="updateInterventionServiceInformationCost"
            @on-change-modality="updateInterventionServiceInformationCost"
            @on-change-sub-discipline="onChangeSubDisciplineHandler"
            @select-sub-discipline="interventionInfo.subDiscipline = $event"
          />
        </validation-observer>

        <div v-if="showCompensatoryRequests">
          <section-title
            class="mt-3 compensatory-description"
            content="Descripción del Servicio Compensatorio"
            :has-line="true"
          />

          <compensatory-request
            :has-permission-closing-analysis="true"
            :has-submit-actions="true"
            :items="compensatoryRequest.data"
            :open-closing-analysis="true"
            :pagination="compensatoryRequest.pagination"
            :same-context="true"
            @on-go-to-page="onGoToNextPageCompensatoryRequest"
            @on-open-closing-analysis="onActiveTabClosingAnalysis($event)"
            @on-save="onRefreshRequestCompensatory"
          />
        </div>
      </custom-tab-item>
      <custom-tab-item
        :active="tabIndex === DocumentTabIndex"
        :lazy="false"
        title="Documentos"
        @click="onChangeInfoTab(DocumentTabIndex)"
      >
        <required-documents
          ref="requiredDocumentsRef"
          :academic-year-id="academicYearId"
          class="mt-4"
          :contract-id="contractId"
          :contract-status-id="contractStatusId"
          :documents="requiredDocuments"
          :is-amendment="isAnAmendment"
          :is-read-only="isReadOnly"
          @on-donwload-approval-letter-report="
            onDownloadApprovalLetterReportHandler
          "
          @on-download-blank-pei-report="onDownloadBlankPeiReport"
          @on-download-file="onDownloadFileHandler"
          @on-download-license-documents="onDownloadLicenseDocumentsHandler"
          @on-download-pei-report="onDownloadPeiReportHandler"
          @on-refresh-document="onRefreshDocumentHandler"
        />
        <DocumentsRelated :contract-id="contractId" />
      </custom-tab-item>
      <custom-tab-item
        v-if="showSignTab || specialParticipantsEdit"
        :active="tabIndex === ContractSigningTagIndex"
        :lazy="false"
        title="Firma de Contrato"
        @click="onChangeInfoTab(ContractSigningTagIndex)"
      >
        <validation-observer
          ref="contractManagementMunicipalitySignature"
          tag="form"
        >
          <contract-signature
            ref="ContractSignatureRef"
            :company-id="companyId"
            :contract-id="contractId"
            :contract-info="contractHeaderInfo"
            :contract-information="contractInformation"
            :contract-information-model="contractInformationModel"
            :is-electronics-signature="
              contractInformation.isElectronicsSignature
            "
            :provider-selected="providerSelected"
            :request-service-type-id="this.requestServiceDisciplineTypeId"
            :signatures-updated="signatures"
            :signatures-updated-director="directors"
            :signatures-updated-teacher="signatures"
            :student-sie-id="contractHeaderInfo.studentSieId"
            :teachers-updated="teachers"
            @on-refresh-contractor-info="handleOnRefreshContractorInfo"
            @update-signatures="updateSignatures"
            @update-signatures-director="updateSignaturesDirector"
            @update-signatures-provider="updateSignaturesProvider"
            @update-signatures-teacher="updateSignaturesTeacher"
          />
        </validation-observer>
      </custom-tab-item>
      <custom-tab-item
        v-if="showSignatureHistoryTab && !specialParticipantsEdit"
        :active="tabIndex === ContractHistoryTagIndex"
        :lazy="false"
        title="Historial de Firmas"
        @click="onChangeInfoTab(ContractHistoryTagIndex)"
      >
        <contract-history
          ref="requiredSignDocumentsRef"
          :company-id="companyId"
          :contract-approval-date="contractInfo?.contractStatus?.date"
          :contract-document-id-signature="
            contractInfo.electronicsSignatureDocumentId
          "
          :contract-document-signature="contractDocumentSignature"
          :contract-id="contractId"
          :contract-status-id="contractStatusId"
          :is-electronic-signature="contractInformation.isElectronicsSignature"
          :request-service-type-id="this.requestServiceDisciplineTypeId"
          :show-upload-document-button="inSignProcess"
          @document-signed="handleDocumentSigned"
          @on-refresh-document="onRefreshDocumentHandler"
        />
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
//lib components
import moment from 'moment';
import _ from 'lodash';
import objectToFormData from 'object-to-formdata';
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import { ValidationObserver } from 'vee-validate';
import DropDown from 'dropdown';
import ActionButton from 'ActionButton';
import { CustomTab, CustomTabItem } from 'custom-tabs';

//local components
import {
  GeneralInfo,
  LocationInfo,
  InterventionInfo,
  ContractInformation,
  InterventionService,
  RequiredDocuments,
  ContractDetermination,
  ContractAmendment,
  DocumentsRelated,
  ContractHistory,
  CancelElectronicSignature,
} from './components/contract-management';

import ContractSignature from './components/contract-management/ContractSignature.vue';
import CompensatoryRequest from './components/RequestCompensatoryResultRow.vue';
import RequestEducationalServiceInfoContract from './components/contract-management/RequestEducationalServiceInfoContract.vue';

//services
import {
  getContractServiceInfo as _getContractServiceInfo,
  getContractServiceSpecialistsValidity as _getContractServiceSpecialistsValidity,
  getProviderLocationTypes as _getProviderLocationTypes,
  getProviderMunicipalities as _getProviderMunicipalities,
  getProviderLocations as _getProviderLocations,
  saveOrUpdateContract as _saveOrUpdateContract,
  submitContract as _submitContract,
  getSpecialists as _getSpecialists,
  getSpecialistSupervisors as _getSpecialistSupervisors,
  getContractDocuments as _getContractDocuments,
  signContract as _signContract,
  getDeterminationReasonsByStatus as _getDeterminationReasonsByStatus,
  updateContractStatus as _updateContractStatus,
  getAmendmentReasons as _getAmendmentReasons,
  amendContract as _amendContract,
  getContractAmendmentReasons as _getContractAmendmentReasons,
  getSpecialistLicenseDocuments as _getSpecialistLicenseDocuments,
  getUserInformation as _getUserInformation,
  updateSignatureContractorInfo as _updateSignatureContractorInfo,
  getTeachers as _getTeachers,
  getSignaturesDirector as _getSignaturesDirector,
  saveInterventionServiceInformation as _saveInterventionServiceInformation,
  cancelContractElectronicSignature as _cancelContractElectronicSignature,
} from '@/services/uspqrp/contract.api.js';

import {
  getCompanyById as _getCompanyById,
  isCompanyApproved as _isCompanyApproved,
} from '@/services/provider/provider.api.js';

import { getDetailInfo as _getLocationDetail } from '@/services/provider/location-service.api.js';
import { IsValidateInvoiceJuly as _IsValidateInvoiceJuly } from '@/services/provider/billing-contract.api.js';

import {
  getFrequencies as _getFrequencies,
  getDurations as _getDurations,
  getModalities as _getModalities,
} from '@/services/common/code.api.js';
import ReportServices from '@/services/common/report.api';
import {
  downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId,
  downloadDocumentByAttachment as _downloadDocumentByAttachment,
} from '@/services/common/document.api.js';

import { searchByContractId as _searchByContractId } from '@/services/provider/compensatoryRequest.api.js';
import { getByParameterName } from '@/services/common/sysparameter.api';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import utils from '@/utils/utilities';

//constants
import addressTypes from '@/utils/constants/addressTypes.js';
import rpContractDocumentTypes from '@/utils/constants/rpContractDocumentTypes.js';
import disciplineTypes from '@/utils/constants/disciplineTypes.js';
import codeLocationTypes from '@/utils/constants/codeLocationTypes.js';
import specialtyTypes from '@/utils/constants/specialtyTypes.js';
import rpContractStatus from '@/utils/constants/rpContractStatus.js';
import roles from '@/utils/constants/roles.js';
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import serviceTypes from '@/utils/constants/serviceTypes.js';
import rpContractDeterminationReasons from '@/utils/constants/rpContractDeterminationReasons.js';
import rpContractAmendmentReasons from '@/utils/constants/rpContractAmendmentReasons.js';
import { getClosingAnalysisByIdRequest as _getClosingAnalysisByIdRequest } from '@/services/provider/closing-analysis.api';
import viewCompensatoryDisplay from '@/utils/constants/compensatoryRequestView';
import {
  getSignatures as _getSignatures,
  createDocumentForSignature as _createDocumentForSignature,
} from '@/services/uspqrp/signatures-contractor.api';
const CONTRACT_MANAGEMENT_ROUTE_NAME = 'contractManagement';
const DEFAULT_REQUEST_COMPENSATORY_PAGE_INDEX = 1;
const DEFAULT_REQUEST_COMPENSATORY_PAGE_SIZE = 5;
import ContractManagementTabs from '@/utils/constants/contractManagementTabs';
import docDocumentTypes from '@/utils/constants/docDocumentTypes';
import { getDeviceInformation } from '@/utils/deviceInfoUtil';
import { name as userModuleName } from '@/store/modules/user';
import { mapState } from 'vuex';
import { eSignatureInitAcademicYear } from '@/utils/constants/eSignatureInitAcademicYear';
import { SIGNATURE_PROJECT_ID } from '@/config';
import { getParentsSignatures as _getParentsSignatures } from '@/services/uspqrp/provisional-remedy.api.js';
import servicesTypes from '@/utils/constants/serviceTypes.js';
import {
  getProviderSelected as _getProviderSelected,
  validateContractForSign as _validateContractForSign,
} from '@/services/uspqrp/contract.api';
import CustomTextArea from 'CustomTextArea';
import CustomCallout from 'custom-callout';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus';

export default {
  name: 'ContractManagement',
  mixins: [alertMixin],
  components: {
    ValidationObserver,
    PageHeader,
    SectionTitle,
    DropDown,
    ActionButton,
    GeneralInfo,
    LocationInfo,
    InterventionInfo,
    InterventionService,
    RequiredDocuments,
    ContractInformation,
    ContractDetermination,
    ContractAmendment,
    DocumentsRelated,
    CompensatoryRequest,
    CustomTab,
    CustomTabItem,
    ContractSignature,
    ContractHistory,
    CancelElectronicSignature,
    RequestEducationalServiceInfoContract,
    CustomTextArea,
    CustomCallout,
  },
  data: () => ({
    isDisabled: false,
    tabIndex: ContractManagementTabs.CONTACT_INFORMATION,
    moduleName: storeModuleNames.UspqrpContractSearchRequestModuleName,
    locationTypes: [],
    contacts: [],
    contractInfo: {},
    contractHeaderInfo: {},
    locationInfo: {},
    serviceInfo: {},
    peiInfo: {},
    originalPeiInfo: {},
    contractInformation: {},
    interventionServiceModel: {},
    contractInformationModel: {},
    contractDocumentSignature: {},
    requiredDocuments: [],
    validateSpecialist: true,
    catalog: {
      locationTypes: [],
      municipalities: [],
      locations: [],
      frequencies: [],
      durations: [],
      modalities: [],
      specialists: [],
      supervisors: [],
      supervisedSpecialtyTypes: [
        specialtyTypes.OccupationalTherapyAssistant,
        specialtyTypes.PhysicalTherapyAssistant,
        specialtyTypes.SpeechAndLanguageTherapist,
      ],
      amendmentReasons: [],
    },
    interventionInfo: {
      specialists: [],
      subDiscipline: {},
    },
    locationMatch: null,
    locationType: null,
    determinationData: {
      status: rpContractStatus.Unset,
      reasons: [],
      titleClass: null,
      texts: {
        infinitive: null,
        participle: null,
        action: null,
      },
      isReadOnly: false,
      contractStatus: {},
    },
    cancelElectronicSignatureData: {
      status: rpContractStatus.ApprovedContractInformation,
      reason: null,
      isReadOnly: false,
    },
    subDisciplineTypeId: null,
    viewAmendContract: false,
    amendContractData: {
      selectedReasons: [],
      amendmentContractComment: '',
    },
    amendReadOnlyContractData: {
      selectedReasons: [],
      amendmentContractComment: '',
    },
    readOnlyForInterventionService: false,
    compensatoryRequest: {
      data: [],
      pagination: {},
    },
    aeeFeaturesEnabled: false,
    disablePrintAndSignButtonBaseOnDisciplineTypeAndUserRole: false,
    signatures: [],
    signaturesTeacher: [],
    signaturesDirector: [],
    signaturesProvider: [],
    hasAtLeastOneSignature: false,
    showCancelElectronicSignature: false,
    deviceInformation: {
      ip: null,
      browser: null,
      engine: null,
      device: null,
      os: null,
    },
    specialParticipantsEdit: false,
    studentInformation: {
      studentName: null,
      regStudent: null,
      impedimentDescription: null,
      impedimentId: null,
      isStudentEgressed: true,
      location: {
        locationType: { name: '' },
        region: { name: '' },
        district: { name: '' },
        municipality: { name: '' },
        school: { name: '' },
        name: null,
      },
    },
    teachers: [],
    directors: [],
    providerSelected: null,
    deniedComment: null,
    contractInfoBody: {},
    contractCostChangeLogDto: {},
  }),
  computed: {
    locationDefaultValue() {
      return this.studentInformation.location;
    },
    ContactInformationTabIndex() {
      return ContractManagementTabs.CONTACT_INFORMATION;
    },
    DocumentTabIndex() {
      return ContractManagementTabs.DOCUMENTS;
    },
    ContractSigningTagIndex() {
      return ContractManagementTabs.CONTRACT_SIGNING;
    },
    ContractHistoryTagIndex() {
      return ContractManagementTabs.CONTRACT_HISTORY;
    },
    headerConfig() {
      return {
        title: 'Manejo de Contratos para Servicios',
        icon: 'fas fa-clipboard-list',
        routes: [
          'Inicio',
          'Proveedores',
          'Contrato',
          'Manejo de Contratos para Servicios',
          `${this.contractId ? 'Ver' : 'Añadir'}`,
        ],
      };
    },
    determinationActionItems() {
      return [
        {
          label: 'Aprobar',
          icon: 'fas fa-check color-green',
          visible: this.canApproveContract,
          click: () =>
            this.setViewToDisplay(rpContractStatus.ApprovedContractInformation),
        },
        {
          label: 'Denegar',
          icon: 'fas fa-times-circle color-primary-4',
          visible: this.canDenyContract,
          click: () =>
            this.setViewToDisplay(rpContractStatus.DeniedContractInformation),
        },
      ];
    },
    contractId() {
      return this.$route.params?.contractId
        ? Number(this.$route.params?.contractId)
        : null;
    },
    contractNumber() {
      return this.contractHeaderInfo?.contractNumber;
    },
    contractStatusId() {
      return this.contractHeaderInfo?.contractStatusId;
    },
    showSignTab() {
      return (
        this.contractStatusId != rpContractStatus.InSigningProcess &&
        this.contractStatusId != rpContractStatus.ApprovedContractInformation &&
        this.contractStatusId != rpContractStatus.Signed &&
        this.contractStatusId != rpContractStatus.ContractAmended &&
        this.academicYearId >= eSignatureInitAcademicYear
      );
    },
    showSignatureHistoryTab() {
      return (
        this.contractStatusId == rpContractStatus.InSigningProcess ||
        this.contractStatusId == rpContractStatus.ApprovedContractInformation ||
        this.contractStatusId == rpContractStatus.Signed ||
        this.contractStatusId == rpContractStatus.ContractAmended
      );
    },
    companyId() {
      return Number(this.$route.params?.companyId);
    },
    academicYearId() {
      return Number(this.contractHeaderInfo?.academicYearId);
    },
    requestServiceId() {
      return Number(this.$route.params?.requestServiceId);
    },
    requestServiceTypeId() {
      return Number(this.contractHeaderInfo?.requestServiceTypeId);
    },
    requestServiceDisciplineTypeId() {
      return this.contractHeaderInfo?.requestServiceDisciplineTypeId;
    },
    isNewContract() {
      return Boolean(this.$route.query?.isNewContract);
    },
    hasContractInProgress() {
      return this.contractInfo?.hasContractInProgress || false;
    },
    hasPrimaryContract() {
      return this.contractInfo?.hasPrimaryContract || false;
    },
    nextContractIsExtendedService() {
      return this.contractInfo?.nextContractIsExtendedService || false;
    },
    isReadOnly() {
      const validStatus = [
        rpContractStatus.New,
        rpContractStatus.DeniedContractInformation,
      ];

      return (
        this.contractId &&
        !validStatus.includes(this.contractHeaderInfo?.contractStatusId)
      );
    },
    showSaveAndSubmit() {
      return (
        this.contractHeaderInfo?.contractStatusId !=
        rpContractStatus.ContractCanceled
      );
    },
    isReadOnlyForLocationInfo() {
      const selectedReasons = this.amendReadOnlyContractData?.selectedReasons;

      if (
        this.isAnAmendment &&
        this.contractHeaderInfo?.contractStatusId === rpContractStatus.New
      ) {
        return (
          this.contractId &&
          !selectedReasons.some(
            (x) => x.id == rpContractAmendmentReasons.ChangePlaceOfService
          )
        );
      }

      return this.isReadOnly;
    },
    isReadOnlyForInterventionInfo() {
      const selectedReasons = this.amendReadOnlyContractData?.selectedReasons;

      if (
        this.isAnAmendment &&
        this.contractHeaderInfo?.contractStatusId === rpContractStatus.New
      ) {
        return (
          this.contractId &&
          !selectedReasons.some(
            (x) =>
              x.id ==
              rpContractAmendmentReasons.SpecialistChangedEliminatedOrAdded
          ) &&
          !selectedReasons.some(
            (x) => x.id == rpContractAmendmentReasons.ChangePlaceOfService
          )
        );
      }

      return this.isReadOnly;
    },
    isReadOnlyForInterventionService() {
      const selectedReasons = this.amendReadOnlyContractData?.selectedReasons;
      if (
        this.isAnAmendment &&
        this.contractHeaderInfo?.contractStatusId === rpContractStatus.New
      ) {
        return (
          this.contractId &&
          !selectedReasons.some(
            (x) =>
              x.id == rpContractAmendmentReasons.ChangeFrequencyDurationModality
          )
        );
      }

      return this.isReadOnly;
    },
    serviceDisciplineName() {
      return this.contractHeaderInfo.requestServiceDisciplineTypeName
        ? `${this.contractHeaderInfo.requestServiceTypeName} - ${this.contractHeaderInfo.requestServiceDisciplineTypeName}`
        : this.contractHeaderInfo.requestServiceTypeName;
    },
    interventionServiceInfoMatchWithPei() {
      if (this.contractInfo.isTherapyServiceType) {
        return this.contractInfo.contractMatchWithPei;
      }
      if (
        this.contractInfo.isAssistanceServiceType &&
        this.contractInfo.hasInvalidService
      ) {
        return false;
      }

      return true;
    },
    canDownloadContract() {
      return (
        (this.contractId &&
          [
            rpContractStatus.InSigningProcess,
            rpContractStatus.ApprovedContractInformation,
          ].includes(this.contractHeaderInfo?.contractStatusId) &&
          !this.contractInformation.isElectronicsSignature) ??
        false
      );
    },
    canCreateSignDocument() {
      return (
        (this.contractId &&
          [rpContractStatus.ApprovedContractInformation].includes(
            this.contractHeaderInfo?.contractStatusId
          ) &&
          this.contractInformation.isElectronicsSignature) ??
        false
      );
    },
    canEditParticipants() {
      const isValidForElectronic =
        (this.contractId &&
          [rpContractStatus.ApprovedContractInformation].includes(
            this.contractHeaderInfo?.contractStatusId
          ) &&
          this.contractInformation.isElectronicsSignature) ??
        false;

      const isValidForManuscrito =
        this.contractId &&
        [rpContractStatus.InSigningProcess].includes(
          this.contractHeaderInfo?.contractStatusId
        ) &&
        !this.contractInformation.isElectronicsSignature;

      return isValidForElectronic || isValidForManuscrito;
    },
    canSignContract() {
      return (
        this.contractId &&
        [
          rpContractStatus.ApprovedContractInformation,
          rpContractStatus.InSigningProcess,
        ].includes(this.contractStatusId) &&
        this.userHasPermissions('rempro.providers.contract.signcontract.view')
      );
    },
    showContractDetermination() {
      return this.determinationData.status !== rpContractStatus.Unset;
    },
    canApproveContract() {
      return (
        this.contractId &&
        this.userHasPermissions('rempro.providers.contract.approve') &&
        this.contractStatusId === rpContractStatus.Submitted &&
        !this.showContractDetermination
      );
    },
    canDenyContract() {
      return (
        this.contractId &&
        this.userHasPermissions('rempro.providers.contract.deny') &&
        this.contractStatusId === rpContractStatus.Submitted &&
        !this.showContractDetermination
      );
    },
    canCancelContract() {
      if (this.showContractDetermination && !this.determinationData.isReadOnly)
        return false;

      let result =
        this.contractId &&
        this.userHasPermissions('rempro.providers.contract.cancel') &&
        this.contractStatusId !== rpContractStatus.ContractCanceled &&
        (this.academicYearId == this.currentAcademicYearId
          ? true
          : // if the contract is not in the current academic year, then it can only be canceled if it is in the status of "Informacion Aprobada" and Evaluation
            ([rpContractStatus.ApprovedContractInformation].includes(
              this.contractStatusId
            ) &&
              this.contractInfo.isEvaluationServiceType) ||
            ([rpContractStatus.Signed].includes(this.contractStatusId) &&
              this.contractInfo.isEvaluationServiceType &&
              !this.contractInformation.isBilled) ||
            ([rpContractStatus.InSigningProcess].includes(
              this.contractStatusId
            ) &&
              this.contractInformation.isElectronicsSignature));

      if (this.isProveedorRP) {
        result =
          result &&
          [
            rpContractStatus.New,
            rpContractStatus.Submitted,
            rpContractStatus.ApprovedContractInformation,
            rpContractStatus.InSigningProcess,
            rpContractStatus.DeniedContractInformation,
            rpContractStatus.Signed,
            rpContractStatus.ContractAmended,
            rpContractStatus.InSigningProcess,
            rpContractStatus.Signed,
          ].includes(this.contractStatusId);
      }
      if (
        this.contractInfo.isTherapyServiceType ||
        this.contractInfo.isEvaluationServiceType
      ) {
        result =
          result &&
          [
            rpContractStatus.New,
            rpContractStatus.Submitted,
            rpContractStatus.ApprovedContractInformation,
            rpContractStatus.InSigningProcess,
            rpContractStatus.DeniedContractInformation,
            rpContractStatus.Signed,
          ].includes(this.contractStatusId);

        //TC balance
        if (this.contractInfo.isTherapyCompensatory) {
          result =
            result && this.contractInfo.compensatoryServicePendingBalance > 0;
        }
      }

      if (this.canCancelElectronicSignature) {
        return true;
      } else {
        return result;
      }
    },
    canCancelElectronicSignature() {
      return (
        this.contractStatusId === rpContractStatus.InSigningProcess &&
        this.contractInformation.isElectronicsSignature
      );
    },
    isProveedorRP() {
      return this.userRoleId === roles.Provider_RP;
    },
    cantNotBeSuppliedInSchoolOrHome() {
      if (
        this.requestServiceDisciplineTypeId &&
        this.locationInfo?.locationType?.id
      ) {
        return (
          this.requestServiceDisciplineTypeId ===
            disciplineTypes.TerapiaVisual &&
          [codeLocationTypes.School, codeLocationTypes.Home].includes(
            this.locationInfo.locationType.id
          )
        );
      }
      return false;
    },
    userCanAmendContract() {
      return this.userHasPermissions('rempro.providers.contract.amendcontract');
    },
    canAmendContract() {
      return (
        (this.contractStatusId === rpContractStatus.Signed &&
          [
            serviceTypes.Therapy,
            serviceTypes.Assistant,
            serviceTypes.Nurse,
          ].includes(this.requestServiceTypeId) &&
          !this.hasContractInProgress) ||
        this.nextContractIsExtendedService
      );
    },
    isAnAmendment() {
      return this.contractHeaderInfo.isAmendment;
    },
    isValidCost() {
      return (
        this.contractInformationModel.cost <=
        parseInt(this.contractInformationModel.maxServiceCost)
      );
    },
    workdayDateTime() {
      if (this.contractInformationModel.workday) {
        const workdayHourMinutes = this.contractInformationModel.workday.split(
          ':'
        );
        const hours = Number(workdayHourMinutes[0]);
        const minutes = Number(workdayHourMinutes[1]);

        const _date = moment({
          hour: hours,
          minute: minutes,
        })
          .format()
          .slice(0, -6);

        return _date;
      }

      return null;
    },
    isSchoolOrHome() {
      if (this.locationType)
        return [codeLocationTypes.School, codeLocationTypes.Home].includes(
          this.locationType
        );
      return false;
    },
    viewCompensatoryDisplayEnum() {
      return viewCompensatoryDisplay;
    },
    requestCompensatoryId() {
      return this.$route.params?.requestCompensatoryId
        ? Number(this.$route.params?.requestCompensatoryId)
        : null;
    },
    inSignProcess() {
      return (
        this.contractStatusId == rpContractStatus.InSigningProcess ||
        this.contractStatusId == rpContractStatus.ApprovedContractInformation ||
        this.contractStatusId == rpContractStatus.Signed
      );
    },
    hasUploadContractSignedDocumentDOC() {
      return (
        this.contractDocumentSignature &&
        this.contractDocumentSignature.attachment !== null
      );
    },
    isESignatureInitcAcademicYear() {
      return this.academicYearId >= eSignatureInitAcademicYear;
    },
    hasValidSignDateDOC() {
      return Boolean(this.contractDocumentSignature?.signDate);
    },
    isDisabledPrintAndSignButtons() {
      const validStatus = [
        rpContractStatus.InSigningProcess,
        rpContractStatus.ApprovedContractInformation,
      ];

      const invalidRoles = [
        roles.Provider_RP,
        roles.Assistant_Provider_RP,
        roles.Specialist_RP,
      ];
      return (
        this.isDisabled ||
        (this.contractId &&
          validStatus.includes(this.contractHeaderInfo?.contractStatusId) &&
          this.requestServiceDisciplineTypeId ===
            disciplineTypes.ServiceAssistant &&
          this.requestServiceTypeId === serviceTypes.Assistant &&
          invalidRoles.includes(this.userRoleId) &&
          this.disablePrintAndSignButtonBaseOnDisciplineTypeAndUserRole)
      );
    },
    providerLocationMunicipality() {
      return this.locationInfo.municipalityId
        ? {
            id: this.locationInfo.municipalityId,
            name: this.locationInfo.municipalityName,
          }
        : null;
    },
    showCompensatoryRequests() {
      return (
        ![serviceTypes.Evaluation, serviceTypes.Assistant].includes(
          this.requestServiceTypeId
        ) && this.aeeFeaturesEnabled
      );
    },
    ...mapState(userModuleName, ['user']),
    canCommentDeniedContract() {
      return (
        this.contractHeaderInfo?.contractStatusId ==
        rpContractStatus.DeniedContractInformation
      );
    },
    canEditCostInAllStatus() {
      if (
        this.contractStatusId != rpContractStatus.New &&
        !this.contractInfoBody.isElectronicsSignature
      ) {
        return this.userHasPermissions('rempro.servicecost.edit');
      }
      var allowedStatus = [
        rpContractStatus.New,
        rpContractStatus.DeniedContractInformation,
        rpContractStatus.ApprovedContractInformation,
      ];
      return (
        this.userHasPermissions('rempro.servicecost.edit') &&
        allowedStatus.includes(this.contractStatusId)
      );
    },
  },
  async mounted() {
    await this.getProviderSelected();
    await this.setDeviceInformation();
    await this.getAEESysParameter();
    await this.getDisabledPrintAndAsignButtonSysParameter();
    await this.onLoad();
    const { toCloseAnalysis } = this.$route.params;
    if (toCloseAnalysis) {
      const compensatoryDescriptionElement = document.querySelector(
        '.compensatory-description'
      );
      compensatoryDescriptionElement.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      });
    }
    this.isDisabled = false;
  },
  methods: {
    async onLoad() {
      await this.getServiceCatalog();
      await this.getContractServiceInfo();
      await this.getProviderLocationTypes(this.companyId, this.academicYearId);
      await this.onSearchCompensatoryRequest(this.contractId);
      await this.getSignatures();
      await this.getTeachers();
      await this.getDirectors();
    },
    setCollections(collections) {
      this.applicantCategories = [...collections.applicants].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      this.municipalities = collections.municipalities ?? [];
      this.documentTypes = collections.documentTypes ?? [];
      this.locationTypes = collections.locationTypes ?? [];
      this.unAvailableServices = collections.servicesNotAvailables ?? [];
    },
    evalServiceMatch(
      originService,
      destinationService,
      isPrivateStudent,
      hasOpenAdmission
    ) {
      return (
        originService?.frequency?.id === destinationService?.frequency?.id &&
        originService?.duration?.id === destinationService?.duration?.id &&
        ((isPrivateStudent && hasOpenAdmission) || !isPrivateStudent)
      );
    },
    onChangeInfoTab(index) {
      this.tabIndex = index;
      if (
        index === this.ContractHistoryTagIndex &&
        this.contractInformation.isElectronicsSignature &&
        this.contractDocumentSignature &&
        this.contractStatusId === rpContractStatus.InSigningProcess
      ) {
        this.$refs.requiredSignDocumentsRef?.getStatusParticipants();
      }
    },
    async getContractServiceInfo() {
      if (this.companyId && this.requestServiceId) {
        const response = (
          await _getContractServiceInfo(
            this.companyId,
            this.requestServiceId,
            this.contractId
          )
        ).data;

        this.contractInfoBody = response;

        this.studentInformation = response.studentInformation;
        this.setCollections(response.collections);
        this.contractInfo = response;
        this.subDisciplineTypeId = response.subDisciplineTypeId;
        this.contractHeaderInfo = response.contractHeaderInfo;
        this.locationInfo = response.locationInfo;
        this.locationType = this.locationInfo.locationType?.id;
        this.serviceInfo = response.serviceInfo;
        this.deniedComment = response.deniedComment;

        const municipalitySignature =
          response.municipalitySignatureData ??
          this.providerLocationMunicipality;

        this.interventionInfo = {
          ...this.interventionInfo,
          specialists: response.specialists ?? [],
        };

        this.contractInformation = {
          ...this.contractInformation,
          cost: response.contractCost ? response.contractCost : null,
          scheduleAssistantIn: response.scheduleAssistantIn
            ? response.scheduleAssistantIn
            : null,
          scheduleAssistantOut: response.scheduleAssistantOut
            ? response.scheduleAssistantOut
            : null,
          transportationCost: response.contractTransportationCost,
          hasTransportationCost:
            typeof response.contractTransportationCost === 'number'
              ? Boolean(response.contractTransportationCost)
              : this.contractInfo.isAssistanceServiceType
              ? false
              : null,
          applicantName: response.contractContractingNameParty,
          municipalitySignature: municipalitySignature,
          isElectronicsSignature: response.isElectronicsSignature,
          municipalityOfresidence: response.residenceMunicipalityId,
        };
        this.contractHeaderInfo.municipalitySignature = municipalitySignature;
        this.contractHeaderInfo.isElectronicsSignature = response.isElectronicsSignature
          ? response.isElectronicsSignature
          : null;
        if (response.signContract) {
          this.contractDocumentSignature = {
            contractId: response.signContract.contractId,
            signDate: response.signContract.signDate
              ? response.signContract.signDate
              : null,
            filename: response.signContract.filename,
            documentId: response.signContract.documentId,
            documentTypeId: response.signContract.documentTypeId,
            contractDocumentId: response.signContract.contractDocumentId,
          };
        }
        if (this.isAnAmendment) {
          this.getContractAmendmentReasons();
        }
        this.peiInfo = response.peiInfo;
        this.originalPeiInfo = {
          duration: response.peiInfo.duration,
          frequency: response.peiInfo.frequency,
          modality: response.peiInfo.modality,
        };
        this.requiredDocuments = response.requiredDocuments;
        await this.getSpecialists(this.locationInfo?.locationService?.id);
        const hasStatus = Boolean(response.contractStatus);

        const validStatus = [
          rpContractStatus.New,
          rpContractStatus.DeniedContractInformation,
        ];

        if (validStatus.includes(this.contractStatusId)) {
          if (this.locationInfo?.locationType?.id) {
            //get municipalities
            await this.onChangeLocationTypeHandler(
              this.locationInfo?.locationType
            );
            //get location services
            if (this.locationInfo.municipality?.id) {
              await this.onChangeMunicipality({
                locationType: this.locationInfo?.locationType,
                municipality: this.locationInfo.municipality,
              });
            }
            //get location details
            if (this.locationInfo.locationService?.id) {
              await this.onChangeLocationHandler(
                this.locationInfo.locationService
              );
            }
            this.locationInfo = response.locationInfo;
          }
        }

        if (hasStatus) {
          const isStatusDetermination = [
            rpContractStatus.ApprovedContractInformation,
            rpContractStatus.ContractCanceled,
            rpContractStatus.DeniedContractInformation,
          ].includes(response.contractStatus.statusId);

          if (!isStatusDetermination) return;

          this.setViewToDisplay(response.contractStatus.statusId);
          this.determinationData.contractStatus = response.contractStatus;
          this.determinationData.isReadOnly = true;
        }
        if (this.contractStatusId === rpContractStatus.New) {
          if (this.locationInfo?.locationType?.id) {
            //get municipalities
            await this.onChangeLocationTypeHandler(
              this.locationInfo?.locationType
            );

            //get location services
            if (this.locationInfo.municipality?.id) {
              await this.onChangeMunicipality({
                locationType: this.locationInfo?.locationType,
                municipality: this.locationInfo.municipality,
              });
            }

            //get location details
            if (this.locationInfo.locationService?.id) {
              await this.onChangeLocationHandler(
                this.locationInfo.locationService
              );
            }
            this.locationInfo = response.locationInfo;
          }
        }
      }
    },
    async getServiceCatalog() {
      this.catalog.frequencies = (await _getFrequencies()).data;
      this.catalog.durations = (await _getDurations()).data;
      this.catalog.modalities = (await _getModalities()).data;
    },
    async getProviderLocationTypes(companyId, academicYearId) {
      this.catalog.locationTypes = (
        await _getProviderLocationTypes(companyId, academicYearId)
      ).data;
    },
    async onChangeLocationTypeHandler(value) {
      this.catalog.municipalities = [];
      this.catalog.locations = [];
      this.clearDestinyInfo();
      if (value?.id) {
        this.locationType = value.id;
        this.catalog.municipalities = (
          await _getProviderMunicipalities(
            this.companyId,
            this.academicYearId,
            value.id
          )
        ).data;
      }
    },
    clearDestinyInfo() {
      this.locationInfo = {
        ...this.locationInfo,
        destinyAddressLine1: null,
        destinyAddressLine2: null,
        destinyAddressLine3: null,
        destinyMunicipalityName: null,
        destinyAddressState: null,
        destinyAddressZipCode: null,
      };
    },
    async onChangeMunicipality({ locationType, municipality }) {
      this.catalog.locations = [];
      this.clearDestinyInfo();
      if (locationType?.id && municipality?.id) {
        this.catalog.locations = (
          await _getProviderLocations(
            this.companyId,
            this.academicYearId,
            locationType?.id,
            municipality?.id
          )
        ).data;
      }
    },
    async onChangeLocationHandler(value) {
      this.clearDestinyInfo();
      if (value?.id) {
        const response = (await _getLocationDetail(value.id)).data;
        this.contacts = response.contacts;
        if (Object.values(response).length) {
          const principalAddress = response.addresses.find(
            (x) => x.addressTypeCd === addressTypes.Physical
          );
          this.locationInfo = {
            ...this.locationInfo,
            destinyAddressLine1: principalAddress?.addressLine1,
            destinyAddressLine2: principalAddress?.addressLine2,
            destinyAddressLine3: principalAddress?.addressLine3,
            destinyMunicipalityName: principalAddress?.municipalityDescription,
            destinyAddressState: 'PR',
            destinyAddressZipCode: principalAddress?.zipCode,
            contacts: principalAddress?.contacts,
          };
        }

        await this.getSpecialists(value.id);
      }
    },
    onChangeServiceModelHandler(value) {
      this.interventionServiceModel = value;
      this.contractInfo.contractMatchWithPei =
        this.peiInfo.duration?.id ==
          this.interventionServiceModel?.duration?.id &&
        this.peiInfo.frequency?.id ==
          this.interventionServiceModel?.frequency?.id;
    },
    async deletePeiReport(peiId) {
      try {
        await ReportServices.deleteReport(`deletepeireport/${peiId}`);
        this.ShowToast(
          '',
          'El documento de PDF de PEI fue actualizado exitósamente.',
          'success'
        );
      } catch (err) {
        if (err && err.response && err.response.data) {
          this.ShowToast('Error', err.response.data.message, 'error');
        } else {
          this.ShowToast('Error', 'Ha ocurrido un error inesperado.', 'error');
        }
      }
    },
    async onDownloadBlankPeiReport(item) {
      const peiId = item.peiId;
      await this.deletePeiReport(peiId); // actualiza el documento de PEI
      await this.getReportsPEIPS(item); // descarga el documento de PEI
    },
    async onDownloadPeiReportHandler(item) {
      if (item.documentId) {
        return this.onDownloadFileHandler(item.documentId);
      } else {
        await this.getReportsPEIPS(item);
      }
    },
    async getReportsPEIPS(item) {
      try {
        const { data } = await ReportServices.getReport('peiorps', {
          peiId: item.peiId,
        });
        utils.convertToBlob(data);
      } catch (err) {
        this.ShowToast('', err.response?.data?.message, 'error');
      }
    },
    async onDownloadApprovalLetterReportHandler() {
      const info = this.contractInfo;
      await ReportServices.getReport('determinationletter', {
        requestServiceId: info.requestServiceId,
        serviceTypeId: info.requestServiceTypeId,
        serviceStatusId: info.requestServiceStatusId,
        determinationReasonId: info.requestServiceDeterminationReasonId,
        determinationReasonCategoryId:
          info.requestServiceDeterminationReasonCategoryId,
      })
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    onDownloadFileHandler(documentId) {
      if (documentId) {
        _downloadDocumentByAttachmentId(documentId).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
      }
    },
    async onDownloadContract() {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      await ReportServices.getReport('contract', {
        contractId: this.contractId,
        serviceTypeId: this.requestServiceTypeId,
        contractNumber: this.contractHeaderInfo.contractNumber,
        isAmendment: this.isAnAmendment,
        IsInterpreter:
          disciplineTypes.Interprete === this.requestServiceDisciplineTypeId,
      })
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
          this.isDisabled = false;
        });
      this.isDisabled = false;
    },
    async setDeviceInformation() {
      this.deviceInformation = await getDeviceInformation();
    },
    async createDocumentForSignature() {
      if (this.isDisabled) {
        return;
      }
      //Validate if can sign
      var responses = await _validateContractForSign(this.contractId).catch(
        (err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
          return;
        }
      );

      if (!responses || !responses.data) {
        return;
      }
      this.isDisabled = true;
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `El contrato se enviará para su firma mediante el mecanismo de firma electrónica a través del portal de
        <b>DE eSignature</b>. Se notificará a todos los participantes acerca de la creación del documento.`,
        'Sí',
        'No'
      );
      if (!answer) {
        this.isDisabled = false;
        return;
      }

      try {
        const { data: contractReport } = await ReportServices.getReport(
          'contract',
          {
            contractId: this.contractId,
            serviceTypeId: this.requestServiceTypeId,
            contractNumber: this.contractHeaderInfo.contractNumber,
            isAmendment: this.isAnAmendment,
            IsInterpreter:
              disciplineTypes.Interprete ===
              this.requestServiceDisciplineTypeId,
          }
        );

        if (!contractReport.hasErrors) {
          this.signatures = [];
          const response = (await _getSignatures(this.contractId)).data;
          this.signatures = response;
          this.$root.$emit('signatures-updated', this.signatures);

          var signatureProfileInfo = [];
          var orderParticipant = 1;

          // Remove teacher of the list of participants
          const index = this.signatures.findIndex(
            (signature) =>
              signature.roleId !== undefined &&
              signature.roleId !== null &&
              signature.roleId.toLowerCase() === roles.Teacher.toLowerCase()
          );

          if (index !== -1) {
            this.signatures.splice(index, 1);
          }

          // Remove director of the list of participants
          const indexdirector = this.signatures.findIndex(
            (signature) =>
              signature.roleId !== undefined &&
              signature.roleId !== null &&
              signature.roleId.toLowerCase() === roles.Director.toLowerCase()
          );

          if (indexdirector !== -1) {
            this.signatures.splice(indexdirector, 1);
          }

          for (const signature of this.signatures) {
            const userInfo = (await _getUserInformation(signature.userId)).data;
            if (Boolean(signature.isProvider) === false) {
              orderParticipant++;
            }
            signatureProfileInfo.push({
              userId: signature.userId,
              name: userInfo.firstName,
              lastName: userInfo.lastPaternalName,
              userName: userInfo.userName,
              email: userInfo.email,
              fullName: userInfo.fullName,
              secondLastName: userInfo.lastMaternalName || '',
              order:
                Boolean(signature.isProvider) === true ? 1 : orderParticipant,
            });
          }
          if (signatureProfileInfo?.length) {
            let signaturesFiltered = this.signatures;
            if (this.contractInformation.isElectronicSignature) {
              signaturesFiltered = this.signatures.filter((x) => x.userId);
            }

            const cleanString = (str) =>
              str ? str.replace(/\s+/g, ' ').trim() : '';

            const payloadDocumentForSignature = {
              projectId: SIGNATURE_PROJECT_ID,
              typeId: docDocumentTypes.RPContract,
              title: `Contrato: ${this.contractNumber} ${this.contractHeaderInfo.studentFullName}`,
              studentName: this.contractHeaderInfo.studentFullName,
              createdById: this.user.profile.sub,
              createdByName: this.user.profile.givenname,
              createdByRole: this.user.profile.role,
              createdByEmail: this.user.profile.email,
              createdByUserName: this.user.profile.name,
              createdByFirstName: this.user.profile.firstname,
              createdByLastName: this.user.profile.lastpaternalname,
              createdByIsAdUserFlag: this.user.aduserflag === '1',
              relatedId: this.contractId.toString(),
              report: contractReport.content,
              orderParticipants: true,
              listOfParticipants: signaturesFiltered.map((signature) => {
                const userInfomation = signatureProfileInfo.find(
                  (x) => x.userId === signature.userId
                );
                return {
                  peiSignatureId: 0,
                  userId: signature.userId,
                  userName: userInfomation.userName,
                  name: cleanString(userInfomation.name),
                  lastName: cleanString(userInfomation.lastName),
                  secondLastName: cleanString(userInfomation.secondLastName),
                  email: signature.email,
                  position: signature?.isProvider
                    ? 'Proveedor'
                    : signature.relationType,
                  authenticationModeId: 1,
                  order: userInfomation.order,
                };
              }),
              ip: this.deviceInformation.ip,
              browser: this.deviceInformation.browser,
              engine: this.deviceInformation.engine,
              device: this.deviceInformation.device,
              os: this.deviceInformation.os,
            };

            const { data: dataDocument } = await _createDocumentForSignature(
              payloadDocumentForSignature
            );

            if (dataDocument.isSuccess) {
              const payload = objectToFormData(
                {
                  contractId: this.contractId,
                  statusId: rpContractStatus.InSigningProcess,
                  electronicsSignatureDocumentId: dataDocument.documentId,
                },
                { indices: true, nullsAsUndefineds: true }
              );

              const { data } = await _updateContractStatus(payload);

              if (data) {
                this.ShowToast(
                  'Confirmación',
                  'Documento para Firma Electrónica creado exitosamente.',
                  'success'
                );
                this.determinationData.isReadOnly = true;
                this.onLoadContractHandler(this.contractId);
                this.onLoad();
              }
            } else {
              this.ShowToast(
                'Alerta',
                dataDocument.errorMessage || 'error',
                'error'
              );
              this.isDisabled = false;
              return;
            }
          } else {
            this.isDisabled = false;
            this.ShowToast('Alerta', contractReport?.message, 'error');
          }
        }
      } catch (err) {
        this.isDisabled = false;
        this.ShowToast('Alerta', err.response?.data?.message, 'error');
      }
      this.isDisabled = false;
    },
    async saveHandler() {
      const isFormValid = await this.$refs.contractManagementForm.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada en información de Contrato.',
          'error'
        );
        this.onChangeInfoTab(ContractManagementTabs.CONTACT_INFORMATION);
        return;
      }

      if (this.contractInfo.isTherapyCompensatory) {
        const IsValidateInvoiceJuly = (
          await _IsValidateInvoiceJuly(this.academicYearId, this.companyId)
        ).data;

        if (!IsValidateInvoiceJuly) {
          this.ShowToast(
            'Alerta',
            'No puede crear contrato hasta que someta la factura del período de julio.',
            'error'
          );
          return;
        }
      }

      const IsCompanyApproved = (
        await _isCompanyApproved(this.academicYearId, this.companyId)
      ).data;

      if (!IsCompanyApproved) {
        this.ShowToast(
          'Alerta',
          'No pueden someter el mismo hasta que el proveedor no esté aprobado por la unidad.',
          'error'
        );
        return;
      }
      if (this.userHasPermissions('rempro.editschedule')) {
        let isValid = await this.$refs.contractInformationRef.saveSchedule();
        if (!isValid) {
          return;
        }
        if (this.contractInformationModel.workday) {
          const workdayValidation = await this.$refs.contractInformationRef.$refs.workday.validate();

          if (!workdayValidation.valid) {
            this.ShowToast(
              'Alerta',
              workdayValidation.errors[0] || 'error',
              'error'
            );

            return;
          }
        }
      }

      const specialists = this.interventionInfo?.specialists || [];

      if (
        (!specialists.length ||
          specialists.length < this.contractInfo.minSpecialistPerContract) &&
        !this.isReadOnlyForInterventionInfo
      ) {
        this.ShowToast(
          'Alerta',
          'Para guardar la información es requerido al menos un especialista.',
          'error'
        );

        return;
      }
      const attachedDocuments = this.contractId
        ? []
        : this.requiredDocuments
            .filter((x) => Boolean(x.attachment))
            .map((x) => ({
              ...x,
            }));

      const removeTeacherSignature = () => {
        const index = this.signatures.findIndex(
          (signature) =>
            signature.roleId !== undefined &&
            signature.roleId !== null &&
            signature.roleId.toLowerCase() === roles.Teacher.toLowerCase()
        );

        if (index !== -1) {
          this.signatures.splice(index, 1);
        }
      };

      if (this.signaturesTeacher.length > 0) {
        const teacherSignature = this.signaturesTeacher[0];

        removeTeacherSignature();

        const exists = this.signatures.some(
          (signature) =>
            signature.email === teacherSignature.email &&
            signature.userId === teacherSignature.userId
        );

        if (!exists) {
          this.signatures.push(teacherSignature);
        }
      } else {
        removeTeacherSignature();
      }

      if (this.signaturesDirector.length > 0) {
        this.signatures = this.signatures.filter(
          (x) => x.roleId !== roles.Director.toLowerCase()
        );
        this.signatures.push(this.signaturesDirector[0]);
      }

      if (this.signaturesProvider.length > 0) {
        this.signatures = this.signatures.filter((x) => x.isProvider !== true);
        this.signatures.push(this.signaturesProvider[0]);
      }

      let payload = this.fillPayload(
        attachedDocuments,
        specialists,
        this.signatures
      );
      const { data: contractId } = await _saveOrUpdateContract(payload).catch(
        (err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        }
      );

      let action = this.contractId ? 'actualizado' : 'creado';
      if (contractId) {
        this.ShowToast(
          'Proceso Exitoso',
          `El contrato ha sido ${action} exitosamente.`,
          'success'
        );

        this.onLoadContractHandler(contractId);
      }
    },
    async submitHandler() {
      if (
        this.requestServiceTypeId === serviceTypes.Therapy &&
        (!this.interventionServiceModel?.duration?.id ||
          !this.interventionServiceModel?.frequency?.id ||
          !this.interventionServiceModel?.modality?.id)
      ) {
        this.ShowToast('Alerta', 'Favor completar el PEI.', 'error');
        await this.$refs.contractManagementForm3.validate();
        return;
      }
      const isFormValid = await this.$refs.contractManagementForm.validate();

      if (this.specialParticipantsEdit == false) {
        if (!isFormValid) {
          this.ShowToast(
            'Alerta',
            'Favor completar la información solicitada en información de Contrato.',
            'error'
          );
          this.onChangeInfoTab(ContractManagementTabs.CONTACT_INFORMATION);
          return;
        }
      }

      if (this.contractInformationModel?.isElectronicsSignature != null) {
        if (this.contractInformationModel?.isElectronicsSignature === true) {
          if (this.contractId != null) {
            if (this.signatures.filter((x) => x.check == true).length == 0) {
              this.ShowToast(
                'Alerta',
                'El sistema requiere un mínimo de 1 participante para la firma.',
                'error'
              );
              this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
              return;
            }
            if (
              this.signatures.filter(
                (x) => x.isPrimary == true && x.check == true
              ).length == 0
            ) {
              this.ShowToast(
                'Alerta',
                'El sistema requiere un mínimo de 1 participante principal para la firma.',
                'error'
              );
              this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
              return;
            }
          } else {
            this.ShowToast(
              'Alerta',
              'Para poder someter un contrato primero debe guardar los cambios para generar un número de contrato.',
              'error'
            );
            this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
            return;
          }
        }
        if (this.contractInformationModel?.isElectronicsSignature === false) {
          if (this.contractId != null) {
            if (this.signatures.filter((x) => x.check == true).length == 0) {
              this.ShowToast(
                'Alerta',
                'El sistema requiere un mínimo de 1 participante para la firma.',
                'error'
              );
              this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
              return;
            }
            if (
              this.signatures.filter(
                (x) => x.isPrimary == true && x.check == true
              ).length == 0
            ) {
              this.ShowToast(
                'Alerta',
                'El sistema requiere un mínimo de 1 participante principal para la firma.',
                'error'
              );
              this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
              return;
            }
          } else {
            this.ShowToast(
              'Alerta',
              'Para poder someter un contrato primero debe guardar los cambios para generar un número de contrato.',
              'error'
            );
            this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
            return;
          }

          //validate when is new contract to submit
          if (this.specialParticipantsEdit == false) {
            const documentWithoutSubmit = this.$refs.requiredDocumentsRef.documentSubmitted
              .map((s) => s.documentTypeName)
              .flat();

            //Show all documents need before sign Contract
            if (documentWithoutSubmit.length > 0) {
              this.ShowToast(
                'Alerta',
                'Los siguientes: ' +
                  documentWithoutSubmit
                    .toString()
                    .replaceAll(',', ', ')
                    .replace(/,\s*$/, ' o ') +
                  ' son requeridos.',
                'error'
              );
              return;
            }
          }
        }
      } else {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada en firma de contrato.',
          'error'
        );
        this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
        return;
      }

      const { data } = await _getCompanyById(this.companyId);
      let companyContacts = data.contacts;

      if (
        companyContacts.filter(
          (x) =>
            x.isPrimaryContactFlag &&
            (x.position == null || x.position == undefined || x.position == '')
        ).length > 0
      ) {
        this.ShowToast(
          'Alerta',
          'El contacto primario del Proveedor no cuenta con una Posición.',
          'error'
        );
        return;
      }

      const specialists = this.interventionInfo?.specialists || [];
      if (!this.hasPrimaryContract) {
        this.ShowToast(
          'Alerta',
          'No es posible someter el contrato ya que no cuenta con un contacto primario en el perfil del proveedor.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (this.cantNotBeSuppliedInSchoolOrHome) {
        this.ShowToast(
          'Alerta',
          'No es posible brindar servicio en hogar y escuela.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (
        (!specialists.length ||
          specialists.length < this.contractInfo.minSpecialistPerContract) &&
        !this.isReadOnlyForInterventionInfo
      ) {
        this.ShowToast(
          'Alerta',
          'Favor seleccione un especialista para la intervención.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      const {
        data: { hasValidLocationService, hasValidLicense },
      } = await _getContractServiceSpecialistsValidity(
        this.requestServiceId,
        this.contractId
      );

      if (!hasValidLocationService) {
        this.ShowToast(
          'Alerta',
          'Para someter el contrato, es obligatorio que el especialista tenga un lugar de servicio registrado. Por favor, revise y actualice el perfil correspondiente.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (!hasValidLicense) {
        this.ShowToast(
          'Alerta',
          'No es posible someter contrato, ya que el(los) especialista(s) seleccionado(s) cuenta(n) con la fecha de expiración de la(s) licencia(s) vencida(s).',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (this.contractInfo.isTherapyCompensatory) {
        const IsValidateInvoiceJuly = (
          await _IsValidateInvoiceJuly(this.academicYearId, this.companyId)
        ).data;

        if (!IsValidateInvoiceJuly) {
          this.ShowToast(
            'Alerta',
            'No puede crear contrato hasta que someta la factura del período de julio.',
            'error'
          );
          return;
        }
      }

      const IsCompanyApproved = (
        await _isCompanyApproved(this.academicYearId, this.companyId)
      ).data;
      if (!IsCompanyApproved) {
        this.ShowToast(
          'Alerta',
          'No pueden someter el mismo hasta que el proveedor no esté aprobado por la unidad.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      const notHasSupervisor = specialists.some(
        (specialist) =>
          specialist.specialistNeedSupervisor === false &&
          this.catalog.supervisedSpecialtyTypes.some(
            (specialtyTypeId) => specialtyTypeId === specialist.specialtyTypeId
          )
      );

      if (notHasSupervisor) {
        this.ShowToast(
          'Alerta',
          'No es posible someter el contrato, ya que el especialista debe contar con un supervisor.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (
        this.contractInformationModel.cost >
          parseFloat(this.contractInformationModel.maxServiceCost) &&
        !this.canEditCostInAllStatus
      ) {
        this.ShowToast(
          'Alerta',
          'No es posible someter el contrato, ya que el costo del servicio sobrepasa el costo máximo de la configuración.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (
        this.contractInfo?.isTherapyServiceType &&
        this.contractInfo?.isTherapyCompensatory &&
        (!this.contractInfo?.compensatoryServiceCount ||
          this.contractInfo?.compensatoryServiceCount == 0)
      ) {
        this.ShowToast(
          'Alerta',
          'Al momento el servicio no cuenta con Servicio de Terapias Compensadas.',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (!this.isAnAmendment) {
        const noHasCOMPUDocument =
          !this.interventionServiceInfoMatchWithPei &&
          (!this.requiredDocuments ||
            !this.requiredDocuments.some(
              (d) =>
                (d.attachment || d.documentId) &&
                d.documentTypeId === rpContractDocumentTypes.CompuMinute
            ));

        if (noHasCOMPUDocument) {
          this.ShowToast('Alerta', 'La minuta de COMPU es requerida.', 'error');
          return;
        }
      }

      if (this.isAnAmendment) {
        if (this.validateAmendmentContractDocuments()) {
          return;
        }
      }

      const attachedDocuments =
        this.requiredDocuments
          .filter((x) => Boolean(x.attachment))
          .map((x) => ({
            ...x,
          })) || [];

      const removeTeacherSignature = () => {
        const index = this.signatures.findIndex(
          (signature) =>
            signature.roleId !== undefined &&
            signature.roleId !== null &&
            signature.roleId.toLowerCase() === roles.Teacher.toLowerCase()
        );

        if (index !== -1) {
          this.signatures.splice(index, 1);
        }
      };

      if (this.signaturesTeacher.length > 0) {
        const teacherSignature = this.signaturesTeacher[0];

        removeTeacherSignature();

        const exists = this.signatures.some(
          (signature) =>
            signature.email === teacherSignature.email &&
            signature.userId === teacherSignature.userId
        );

        if (!exists) {
          this.signatures.push(teacherSignature);
        }
      } else {
        removeTeacherSignature();
      }

      if (this.contractInformationModel?.isElectronicsSignature != null) {
        if (this.contractInformationModel?.isElectronicsSignature === true) {
          if (
            this.contractInfo.requestServiceTypeId === servicesTypes.Assistant
          ) {
            if (this.signaturesDirector.length > 0) {
              this.signatures = this.signatures.filter(
                (x) => x.roleId !== roles.Director.toLowerCase()
              );
              this.signatures.push(this.signaturesDirector[0]);
            } else {
              this.ShowToast(
                'Alerta',
                'Debe seleccionar un Director Principal para la firma',
                'error'
              );
              return;
            }
          }
        }
      }

      if (this.signaturesProvider.length > 0) {
        this.signatures = this.signatures.filter((x) => x.isProvider !== true);
        this.signatures.push(this.signaturesProvider[0]);
      } else {
        this.ShowToast(
          'Alerta',
          'Debe seleccionar un Proveedor Principal para la firma',
          'error'
        );
        return;
      }

      if (this.requestServiceTypeId === serviceTypes.Assistant) {
        if (this.contractInformationModel?.isElectronicsSignature != null) {
          if (this.contractInformationModel?.isElectronicsSignature === true) {
            if (this.contractId != null) {
              this.signaturesToValidate = (
                await _getSignatures(this.contractId)
              ).data;

              const hasTeacherDB = this.signaturesToValidate.some(
                (signature) =>
                  signature.roleId !== undefined &&
                  signature.roleId !== null &&
                  signature.roleId.toLowerCase() === roles.Teacher.toLowerCase()
              );

              if (
                !hasTeacherDB &&
                (!this.signaturesTeacher || this.signaturesTeacher.length === 0)
              ) {
                this.ShowToast(
                  'Alerta',
                  'El sistema requiere un mínimo de 1 maestro para la firma.',
                  'error'
                );
                this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
                return;
              }
            } else {
              this.ShowToast(
                'Alerta',
                'Para poder someter un contrato primero debe guardar los cambios para generar un número de contrato.',
                'error'
              );
              this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);
              return;
            }
          }
        }
      }
      const payload = this.fillPayload(
        attachedDocuments,
        specialists,
        this.signatures
      );

      if (
        this.canEditCostInAllStatus &&
        this.contractInformationModel.cost >
          parseFloat(this.contractInformationModel.maxServiceCost)
      ) {
        this.$refs.contractInformationRef.saveExceededCost();
      }

      const { data: contractId } = await _submitContract(payload).catch(
        (err) => {
          this.isDisabled = false;
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        }
      );

      if (contractId) {
        if (this.specialParticipantsEdit == true) {
          this.ShowToast(
            'Proceso Exitoso',
            'El contrato ha sido guardado exitosamente.',
            'success'
          );
          this.specialParticipantsEdit = false;
        } else {
          this.ShowToast(
            'Proceso Exitoso',
            'El contrato ha sido sometido exitosamente.',
            'success'
          );
        }

        if (this.isAnAmendment) {
          this.viewAmendContract = false;
        }
        await this.getSignatures();
        this.$emit('signatures-updated', this.signatures);
        this.$emit('signatures-updated-teacher', this.signatures);
        this.$emit('signatures-updated-director', this.signatures);
        this.onLoadContractHandler(contractId);
      }
    },
    onLoadContractHandler(contractId) {
      const isNewContract = this.isNewContract;
      if (contractId) {
        this.$router.replace({
          name: CONTRACT_MANAGEMENT_ROUTE_NAME,
          params: {
            companyId: this.companyId,
            requestServiceId: this.requestServiceId,
            contractId: contractId,
          },
          ...(isNewContract
            ? {
                query: {
                  isNewContract: true,
                },
              }
            : {}),
        });
        this.onLoad();
      }
    },
    fillPayload(documentsPayload, specialists, signatures, isSubmit = false) {
      const payload = {
        contractInfo: {
          contractId: this.contractId,
          academicYearId: this.academicYearId,
          companyId: this.companyId,
          requestServiceId: this.requestServiceId,
          contractCost: this.contractInformationModel?.cost,
          scheduleAssistantIn: this.contractInformationModel.scheduleAssistantIn
            ? this.contractInformationModel.scheduleAssistantIn
            : null,
          scheduleAssistantOut: this.contractInformationModel
            .scheduleAssistantOut
            ? this.contractInformationModel.scheduleAssistantOut
            : null,
          maxContractCost: this.contractInformationModel?.maxServiceCost,
          hasTransportationCost: this.contractInformationModel
            ?.hasTransportationCost,
          transportationCost: this.contractInformationModel?.transportationCost,
          contractingNameParty: this.contractInformationModel?.applicantName,
          residenceMunicipalityId: this.contractInformationModel
            ?.municipalityOfresidence?.id,
          workday: this.workdayDateTime,
          municipalitySignature: this.contractInformationModel
            ?.municipalitySignature?.id,
          isElectronicsSignature: this.contractInformationModel
            ?.isElectronicsSignature,
          StudentSchoolId: this.contractInformationModel.StudentSchoolId,
          StudentSchoolName: this.contractInformationModel.StudentSchoolName,
          deniedComment: this.deniedComment,
        },
        locationService: {
          destinyLocationTypeId: this.locationInfo?.locationType?.id,
          destinyLocationFiscalYearId: this.locationInfo?.locationService?.id,
          destinyAddressLine1: this.locationInfo?.destinyAddressLine1,
          destinyAddressLine2: this.locationInfo?.destinyAddressLine2,
          destinyAddressLine3: this.locationInfo?.destinyAddressLine3,
          destinyAddressZipCode: this.locationInfo?.destinyAddressZipCode,
          matchLocation:
            this.locationInfo.municipalityId ===
            this.locationInfo.municipality?.id,
        },
        intervention: {
          therapyDisciplineId: this.requestServiceDisciplineTypeId,
          frecuencyId: this.interventionServiceModel?.frequency?.id,
          durationId: this.interventionServiceModel?.duration?.id,
          modalityId: this.interventionServiceModel?.modality?.id,
          therapyCompensatoryMaxVisitCount: this.contractInfo
            ?.compensatoryServicePendingBalance,
          isTherapyCompensatory: this.contractInfo?.isTherapyCompensatory,
          serviceMatchWithPei: this.interventionServiceInfoMatchWithPei,
          specialists: specialists,
          subDisciplineTypeId: this.interventionServiceModel?.subDiscipline?.id,
          extendedAcademicYearFlag: this.interventionServiceModel
            ?.extendedAcademicYearFlag,
        },
        documents: documentsPayload,
        signatures: signatures,
      };
      // if signature is electronic, only send the ones that have a user
      if (isSubmit && this.contractInformation.isElectronicsSignature) {
        payload.signatures = this.signatures.filter((x) => x.userId);
      }
      const params = objectToFormData(
        { ...payload },
        { indices: true, nullsAsUndefineds: true }
      );

      documentsPayload.forEach((document, index) => {
        for (const key in document) {
          params.append(`documents[${index}].${key}`, document[key]);
        }
      });

      return params;
    },
    async getSpecialists(locationFiscalYearId) {
      if (!locationFiscalYearId || !this.requestServiceDisciplineTypeId) return;
      const { data } = await _getSpecialists(
        this.requestServiceDisciplineTypeId,
        locationFiscalYearId
      );
      const specialists = _.uniqBy(data, 'id');
      this.catalog.specialists = specialists ?? [];
    },
    async getSpecialistSupervisors({ id: specialistId }) {
      this.catalog.supervisors = [];
      if (!specialistId) return;
      const { data } = await _getSpecialistSupervisors(
        this.companyId,
        this.academicYearId,
        specialistId
      );
      this.catalog.supervisors = data ?? [];
    },
    async removeInterventionInfoSpecialist({ specialistId }) {
      const i = this.interventionInfo.specialists.findIndex(
        (x) => x.specialistId === specialistId
      );
      this.interventionInfo.specialists.splice(i, 1);
      if (this.isAnAmendment) {
        await this.getSpecialists(this.locationInfo?.locationService?.id);
      }
    },
    addInterventionInfoSpecialist(item) {
      this.interventionInfo.specialists.push(item);
    },
    matchLocation(value) {
      this.locationMatch = value;
    },
    onChangeContractInformationModelHandler(value) {
      this.contractInformationModel.applicantName = value.applicantName;
      this.contractInformationModel.cost = value.cost;
      this.contractInformationModel.hasTransportationCost =
        value.hasTransportationCost;
      this.contractInformationModel.hasVisited = value.hasVisited;
      this.contractInformationModel.isBilled = value.isBilled;
      this.contractInformationModel.maxServiceCost = value.maxServiceCost;
      this.contractInformationModel.municipalityOfresidence =
        value.municipalityOfresidence;
      this.contractInformationModel.originalCost = value.originalCost;
      this.contractInformationModel.providerName = value.providerName;
      this.contractInformationModel.scheduleAssistantIn =
        value.scheduleAssistantIn;
      this.contractInformationModel.scheduleAssistantOut =
        value.scheduleAssistantOut;
      this.contractInformationModel.transportationCost =
        value.transportationCost;
      this.contractInformationModel.workday = value.workday;
    },
    async onChangeEducationalServiceInfoModelHandler(value) {
      this.contractInformationModel.StudentSchoolId = value.school.id;
      this.contractInformationModel.StudentSchoolName =
        value.privateLocationName;
      await this.getTeachers();
      await this.getDirectors();
    },
    async onRefreshDocumentHandler() {
      this.requiredDocuments = (
        await _getContractDocuments(
          this.peiInfo.peiId,
          this.requestServiceId,
          this.contractId
        )
      ).data;

      if (this.contractStatusId === rpContractStatus.Signed) {
        const updatedSignedContract = this.requiredDocuments.find(
          (x) => x.documentTypeId === rpContractDocumentTypes.SignedContract
        );

        if (updatedSignedContract) {
          this.contractDocumentSignature.filename =
            updatedSignedContract.filename;
          this.contractDocumentSignature.documentId =
            updatedSignedContract.documentId;
          this.contractDocumentSignature.documentTypeId =
            updatedSignedContract.documentTypeId;
          this.contractDocumentSignature.contractDocumentId =
            updatedSignedContract.contractDocumentId;
        }
      }
    },
    async onSignContractHandler() {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      //Save first and later sign
      if (this.contractStatusId <= 0) {
        await this.saveHandler();
      }

      const documentWithoutSubmit = this.$refs.requiredDocumentsRef.documentSubmittedAEE
        .map((s) => s.documentTypeName)
        .flat();
      //Show all documents need before sign Contract
      if (documentWithoutSubmit.length > 0) {
        this.ShowToast(
          'Alerta',
          'De los siguientes: ' +
            documentWithoutSubmit
              .toString()
              .replaceAll(',', ', ')
              .replace(/,\s*$/, ' o ') +
            ' al menos uno es requerido',
          'error'
        );
        this.isDisabled = false;
        return;
      }

      if (this.contractDocumentSignature.attachment == null) {
        this.ShowToast('Alerta', 'El contrato firmado es requerido.', 'error');
        this.onChangeInfoTab(ContractManagementTabs.CONTRACT_HISTORY);
        this.isDisabled = false;
        return;
      }

      if (!this.hasValidSignDateDOC) {
        this.ShowToast(
          'Alerta',
          'Fecha de Firma de Contrato es requerida.',
          'error'
        );
        this.isDisabled = false;
        this.onChangeInfoTab(ContractManagementTabs.CONTRACT_HISTORY);
        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro(a) que desea firmar el contrato ${this.contractNumber}?`,
        'Sí',
        'No'
      );

      if (!answer) {
        this.isDisabled = false;
        return;
      }

      const payload = objectToFormData({
        contractId: this.contractId,
        signDate: this.contractDocumentSignature.signDate,
        documentTypeId: this.contractDocumentSignature.documentTypeId,
        documentTypeName: this.contractDocumentSignature.documentTypeName,
        filename: this.contractDocumentSignature.filename,
        attachment: this.contractDocumentSignature.attachment,
      });
      const { data: result } = await _signContract(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
        this.isDisabled = false;
      });
      if (result) {
        this.ShowToast(
          'Proceso Exitoso',
          'El contrato ha sido firmado exitosamente.',
          'success'
        );
        await this.getContractServiceInfo();
      }
      this.isDisabled = false;
    },
    async getDeterminationReasonsByStatus() {
      const { data } = await _getDeterminationReasonsByStatus(
        this.determinationData.status
      );
      if (
        this.determinationData.status ===
        rpContractStatus.ApprovedContractInformation
      ) {
        this.determinationData.reasons = [...data].filter(
          (x) => x.id !== rpContractDeterminationReasons.ReasonToTry
        );
      } else if (
        this.determinationData.status ===
        rpContractStatus.DeniedContractInformation
      ) {
        this.determinationData.reasons = [...data].filter(
          (x) => x.id !== rpContractDeterminationReasons.ReasonToDeny
        );
      } else {
        this.determinationData.reasons = [...data];
      }
    },
    async setViewToDisplay(status) {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      this.resetContractDetermination();
      this.determinationData.status = status;
      if (!this.showContractDetermination) return;
      if (status === rpContractStatus.ApprovedContractInformation) {
        this.determinationData.texts.infinitive = 'aprobar';
        this.determinationData.texts.participle = 'aprobado';
        this.determinationData.texts.action = 'aprobación';
        this.determinationData.titleClass = 'fa-check color-orange';
      } else if (status === rpContractStatus.ContractCanceled) {
        this.determinationData.texts.infinitive = 'cancelar';
        this.determinationData.texts.participle = 'cancelado';
        this.determinationData.texts.action = 'cancelación';
        this.determinationData.titleClass = 'fa-times-circle color-red';
      } else if (status === rpContractStatus.DeniedContractInformation) {
        this.determinationData.texts.infinitive = 'denegar';
        this.determinationData.texts.participle = 'denegado';
        this.determinationData.texts.action = 'denegación';
        this.determinationData.titleClass = 'fa-times-circle color-orange';
      }

      await this.getDeterminationReasonsByStatus();
      this.isDisabled = false;
    },
    resetContractDetermination() {
      this.determinationData.status = rpContractStatus.Unset;
      this.determinationData.texts.infinitive = null;
      this.determinationData.texts.participle = null;
      this.determinationData.texts.action = null;
      this.determinationData.isReadOnly = false;
      this.determinationData.titleClass = null;
      this.determinationData.reasons = [];
    },
    async onCancelDetermination(data) {
      if (data) await this.getContractServiceInfo();
      else this.resetContractDetermination();
    },
    async onCancelElectronicSignature() {
      this.showCancelElectronicSignature = false;
    },
    async onSaveContractDetermination(item) {
      const isFormValid = await this.$refs.contractDeterminationForm.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          `Favor completar la información de ${this.determinationData.texts.action} solicitada.`,
          'error'
        );
        return;
      }

      let hasElectronicsSignatureDocument =
        this.contractInfo.electronicsSignatureDocumentId != null;

      let textCancelDS = hasElectronicsSignatureDocument
        ? ' y el documento generado para la firma electrónica'
        : '';

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea <b class="text-capitalize">${this.determinationData.texts.infinitive}</b> el contrato <b class="text-capitalize">${this.contractNumber}</b>${textCancelDS}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const payload = objectToFormData(
        {
          contractId: this.contractId,
          statusId: this.determinationData.status,
          ...item,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      const { data } = await _updateContractStatus(payload);

      if (data) {
        this.ShowToast(
          'Confirmación',
          `El contrato ha sido ${this.determinationData.texts.participle} exitosamente.`,
          'success'
        );
        this.determinationData.isReadOnly = true;
        this.onLoadContractHandler(this.contractId);
      }
    },
    async onSaveCancelElectronicSignature(item) {
      const isFormValid = await this.$refs.cancelElectronicSignatureForm.validate();
      if (!isFormValid || item.comment == null || item.comment === '') {
        this.ShowToast(
          'Alerta',
          `Favor completar la información de cancelación solicitada.`,
          'error'
        );
        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea <b class="text-capitalize">cancelar</b> firma electrónica de contrato ${this.contractNumber}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const { data } = await _cancelContractElectronicSignature(
        this.contractId,
        {
          contractId: this.contractId,
          comment: item.comment,
        }
      );

      if (data) {
        this.ShowToast(
          'Confirmación',
          `Documento para Firma Electrónica cancelado exitosamente.`,
          'success'
        );
        this.cancelElectronicSignatureData.isReadOnly = true;
        this.showCancelElectronicSignature = false;
        this.onLoadContractHandler(this.contractId);
      }
    },
    cancelContract() {
      const { isEvaluationServiceType, hasEvaluationCompletedFlag } = {
        ...this.contractInfo,
      };
      if (isEvaluationServiceType && hasEvaluationCompletedFlag) {
        this.ShowToast(
          'Alerta',
          'No es posible cancelar contrato ya que cuenta con la evaluación completada.',
          'error'
        );
        return;
      }
      this.setViewToDisplay(rpContractStatus.ContractCanceled);
    },
    cancelElectronicSignature() {
      this.showCancelElectronicSignature = true;
    },
    onDownloadDeterminationDocumentHandler({ documentId, attachment }) {
      if (documentId) this.onDownloadFileHandler(documentId);
      else _downloadDocumentByAttachment(attachment);
    },
    onGoBackHandler() {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      if (this.contractId && this.isNewContract) {
        this.disposeStoreModule(
          storeModuleNames.UspqrpContractSearchRequestModuleName
        );
        this.$router.go(-2);
        return;
      }
      this.$router.go(-1);
      this.isDisabled = false;
    },
    onChangeSubDisciplineHandler(value) {
      this.subDisciplineTypeId = value?.id;
    },
    async onAmendContractHandler() {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      this.viewAmendContract = true;
      this.catalog.amendmentReasons = (
        await _getAmendmentReasons(this.contractId)
      ).data;
      this.isDisabled = false;
    },
    async onCreateAmendContractHandler({ amendmentReasons, comment }) {
      if (amendmentReasons.length && comment) {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea Enmendar el contrato ${this.contractNumber}?`,
          'Sí',
          'No'
        );
        if (!answer) return;

        const payload = {
          contractId: this.contractId,
          amendmentReasonIds: [],
          amendmentReasons: amendmentReasons.map((reason) => ({
            reasonId: reason.reasonId,
            subReasonId: reason.subReasonId,
            reasonOtherName: reason.reasonOtherName,
          })),
          comment,
        };

        // Convert payload to FormData
        const formData = objectToFormData(payload, {
          indices: true,
          booleansAsIntegers: true,
          nullsAsUndefineds: true,
        });

        // Handle files separately due to limitations in object-to-formdata version 2.2.1
        amendmentReasons.forEach((reason, index) => {
          if (reason.file instanceof File) {
            formData.append(`amendmentReasons[${index}].file`, reason.file);
          }
        });

        const { data: contractId } = await _amendContract(formData).catch(
          (err) => {
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error'
            );
          }
        );

        if (contractId) {
          this.ShowToast(
            'Proceso Exitoso',
            `El contrato ha sido enmendado exitosamente.`,
            'success'
          );

          this.viewAmendContract = false;
          this.onLoadContractHandler(contractId);
        }
      }
    },
    async getContractAmendmentReasons() {
      const { data } = await _getContractAmendmentReasons(this.contractId);
      if (data) {
        this.amendReadOnlyContractData.selectedReasons = data.amendmentReasons;
        this.amendReadOnlyContractData.amendmentContractComment = data.comment;
      }
    },
    validateAmendmentContractDocuments() {
      const requiredAEE =
        this.amendContractData.selectedReasons.some(
          (x) => x.id == rpContractAmendmentReasons.AEE
        ) &&
        (!this.requiredDocuments ||
          !this.requiredDocuments.some(
            (d) =>
              (d.attachment || d.documentId) &&
              d.documentTypeId ===
                rpContractDocumentTypes.MinuteAndPEIThatEvidenceTheAEERecommendations
          ));

      const requiredCFDM =
        this.amendContractData.selectedReasons.some(
          (x) =>
            x.id == rpContractAmendmentReasons.ChangeFrequencyDurationModality
        ) &&
        (!this.requiredDocuments ||
          !this.requiredDocuments.some(
            (d) =>
              (d.attachment || d.documentId) &&
              d.documentTypeId ===
                rpContractDocumentTypes.ReassessmentEvidencingTheChangeInFrequencyAndItsMinutesOfTheCOMPUMeeting
          ));

      const requiredSCEA =
        this.amendContractData.selectedReasons.some(
          (x) =>
            x.id ==
            rpContractAmendmentReasons.SpecialistChangedEliminatedOrAdded
        ) &&
        (!this.requiredDocuments ||
          !this.requiredDocuments.some(
            (d) =>
              (d.attachment || d.documentId) &&
              d.documentTypeId ===
                rpContractDocumentTypes.AuthorizationLetterFromTheParentsToReceiveTheServiceByAnotherSpecialist
          ));
      if (requiredAEE) {
        this.ShowToast(
          'Alerta',
          'Minuta y PEI que evidencia las recomendaciones AEE es requerida.',
          'error'
        );
        return true;
      }
      if (requiredCFDM) {
        this.ShowToast(
          'Alerta',
          'El documento reevaluación que evidencia el cambio de frecuencia y su minuta de Reunión de COMPU es requerido.',
          'error'
        );
        return true;
      }
      if (requiredSCEA) {
        this.ShowToast(
          'Alerta',
          'El documento carta autorización de los padres para recibir el servicio por otro especialista es requerido.',
          'error'
        );
        return true;
      }

      return false;
    },
    async onDownloadLicenseDocumentsHandler() {
      const specialists = this.interventionInfo.specialists;
      const ids = specialists.map((x) => x.specialistId);
      _getSpecialistLicenseDocuments(ids)
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
    },
    async onSearchCompensatoryRequest(
      contractId,
      pageIndex = DEFAULT_REQUEST_COMPENSATORY_PAGE_INDEX,
      pageSize = DEFAULT_REQUEST_COMPENSATORY_PAGE_SIZE
    ) {
      contractId = 0; // always search by request service Id

      if (this.showCompensatoryRequests) {
        const excludedStatusIds = [
          compensatoryRequestStatus.SolicitudRechazada,
          compensatoryRequestStatus.SolicitudCompletada,
        ];
        const { data: response } = await _searchByContractId(contractId, {
          requestServiceId: this.requestServiceId,
          excludedStatusIds,
          pageIndex,
          pageSize,
        });

        const results = response.data.map((x) => ({
          ...x,
          documents: [],
          strategies: [],
          isOpen: x.requestId == this.requestCompensatoryId,
          viewToDisplay: this.viewCompensatoryDisplayEnum.CLOSING_ANALYSIS,
        }));

        if (this.requestCompensatoryId) {
          const closingAnalysis = await this.getClosingAnalysisByIdRequest(
            this.requestCompensatoryId
          );

          var indexResult = results.findIndex(
            (request) =>
              request.requestId == closingAnalysis.compensatoryRequestId
          );

          results[indexResult] = closingAnalysis;
        }

        this.compensatoryRequest.data = results;
        this.compensatoryRequest.pagination = {
          totalItemCount: response.totalItemCount,
          pageCount: response.pageCount,
          currentPage: response.currentPage,
          count: response.count,
          pageSize,
        };
      }
    },
    async getClosingAnalysisByIdRequest(requestId) {
      const { data } = await _getClosingAnalysisByIdRequest(requestId);

      return {
        ...data,
        academicYear: data.academicYearDescription,
        requestId: data.compensatoryRequestId,
        requestNumber: data.requestNumber,
        comment: data.description,
        statusId: data.statusId,
        status: data.statusName,
        statusColor: data.statusColor,
        dueInterventions: data.owedInterventions,
        origin: data.origin,
        offeredInterventions: data.sessionOffers,
        currentBalance: data.currentBalance,
        isOpen: true,
        viewToDisplay: this.viewCompensatoryDisplayEnum.CLOSING_ANALYSIS,
      };
    },
    async onRefreshRequestCompensatory() {
      await this.onSearchCompensatoryRequest(this.contractId);
    },
    async onActiveTabClosingAnalysis($event) {
      if ($event.isOpen) {
        const closingAnalysis = await this.getClosingAnalysisByIdRequest(
          $event.id
        );
        var indexResult = this.compensatoryRequest.data.findIndex(
          (request) =>
            request.requestId == closingAnalysis.compensatoryRequestId
        );

        closingAnalysis.isOpen = $event.isOpen;
        this.compensatoryRequest.data.splice(indexResult, 1, closingAnalysis);
      }
    },
    async onGoToNextPageCompensatoryRequest($event) {
      this.onSearchCompensatoryRequest(this.contractId, $event);
    },
    async getAEESysParameter() {
      const { data } = await getByParameterName(
        'RPExtendedSchoolYearFeatureFlag'
      );
      if (data) {
        this.aeeFeaturesEnabled = data.value === 'true';
      }
    },
    async getDisabledPrintAndAsignButtonSysParameter() {
      const { data } = await getByParameterName(
        'RPAssistanceContractPrintDisable'
      );
      if (data) {
        this.disablePrintAndSignButtonBaseOnDisciplineTypeAndUserRole =
          data.value === 'true';
      }
    },
    updateSignatures(signatures) {
      this.signatures = signatures;
    },
    updateSignaturesTeacher(signatures) {
      this.signaturesTeacher = signatures;
    },
    updateSignaturesDirector(signatures) {
      this.signaturesDirector = signatures;
    },
    updateSignaturesProvider(signatures) {
      this.signaturesProvider = signatures;
    },
    async getSignatures() {
      if (this.contractId != null) {
        this.signatures = (await _getSignatures(this.contractId)).data;
      }
    },
    async editParticipants() {
      if (this.specialParticipantsEdit) {
        await this.submitHandler();
      } else {
        this.specialParticipantsEdit = true;
        this.onChangeInfoTab(ContractManagementTabs.CONTRACT_SIGNING);

        this.signatures = [];
        this.signatures.length = 0;
        this.$emit('update-signatures', this.signatures);

        const parents = (
          await _getParentsSignatures(this.studentSieId, this.contractId)
        ).data;
        if (this.isElectronicSign) {
          this.signatures = await parents.filter((x) => x.userId !== null);
        } else {
          this.signatures = await parents;
        }
        this.$emit('update-signatures', this.signatures);
      }
    },
    async handleOnRefreshContractorInfo(row) {
      if (this.contractId && row.userId) {
        const response = await _updateSignatureContractorInfo(
          this.contractId,
          row.userId
        ).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
        if (response?.data) {
          await this.getSignatures();
          this.ShowToast(
            '',
            'La información del contratante ha sido actualizada exitosamente.',
            'success'
          );
        }
      }
    },
    async getTeachers() {
      this.teachers = (
        await _getTeachers(
          this.contractInformationModel?.StudentSchoolId ||
            this.studentInformation?.location?.school?.id
        )
      ).data;
    },
    async getDirectors() {
      this.directors = (
        await _getSignaturesDirector(
          this.contractInformationModel?.StudentSchoolId,
          this.contractId
        )
      ).data;
    },
    async getProviderSelected() {
      if (this.contractId != null) {
        this.providerSelected = (
          await _getProviderSelected(this.contractId)
        ).data;
      }
    },
    async updateInterventionServiceInformationCost() {
      const interventionInfoPayload = objectToFormData({
        durationId: this.interventionServiceModel?.duration?.id,
        modalityId: this.interventionServiceModel?.modality?.id,
        frecuencyId: this.interventionServiceModel?.frecuency?.id,
        contractId: this.contractId,
        locationTypeId: this.$refs.contractInformationRef.$props.locationType,
        disciplineTypeId: this.requestServiceDisciplineTypeId,
        serviceTypeId: this.requestServiceTypeId,
      });

      var costConfigResponse = await _saveInterventionServiceInformation(
        interventionInfoPayload
      );
      if (costConfigResponse?.data) {
        this.$refs.contractInformationRef.setCost(
          costConfigResponse.data.rpMaxServiceCost
        );
      } else {
        this.$refs.contractInformationRef.setCost(0);
      }
    },
    async handleDocumentSigned() {
      await this.onLoad();
    },
  },
};
</script>

<style scoped lang="scss">
.dropdown-custom-action ::v-deep {
  .dropdown {
    button {
      padding: 5px 15px 5px 15px;
    }
  }
}
</style>
